import { useState } from "react";
import type { AppSettings } from "@/lib/queries";
import { useUpdateAppSettings } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";
import { INDIAN_STATES } from "@emr/shared/constants";

export function ClinicProfileCard({ settings, canEdit }: { settings: AppSettings; canEdit: boolean }) {
  const [form, setForm] = useState({
    clinicName: settings.clinicName ?? "",
    tagline: settings.tagline ?? "",
    address: settings.address ?? "",
    phone: settings.phone ?? "",
    email: settings.email ?? "",
    state: settings.state ?? "",
    pincode: settings.pincode ?? "",
  });
  const update = useUpdateAppSettings();

  const save = async () => {
    try {
      await update.mutateAsync(form);
    } catch { /* handled */ }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500">Clinic Profile</CardTitle>
        {canEdit && (
          <Button onClick={save} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label>Clinic Name</Label>
          <Input value={form.clinicName} disabled={!canEdit} onChange={(e) => setForm({ ...form, clinicName: e.target.value })} />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input value={form.tagline} disabled={!canEdit} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <Label>Address</Label>
          <Textarea value={form.address} disabled={!canEdit} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
        </div>
        <div>
          <Label>Phone</Label>
          <Input value={form.phone} disabled={!canEdit} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} disabled={!canEdit} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <Label>State</Label>
          <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })} disabled={!canEdit}>
            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>
              {INDIAN_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Pincode</Label>
          <Input value={form.pincode} disabled={!canEdit} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
        </div>
      </CardContent>
    </Card>
  );
}
